import React, { useEffect, useMemo, useState } from 'react';
import {
  Menu,
  MenuItem,
  Divider,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Typography,
  Avatar,
} from '@mui/material';
import type { users } from '@techgitter/contracts';
import { useUserColors } from '../../hooks/useUserColors';
import { initialsFromName } from './workItemToRow';

interface Candidate {
  name: string;
  email?: string;
}

export interface ReassignMenuProps {
  /** Cursor anchor for the menu; `null` keeps it closed. */
  anchorPosition: { top: number; left: number } | null;
  /** The ticket's current assignee — excluded from the list. */
  currentAssignee: string;
  /** Technicians that currently have tickets (the default list). */
  assigneesWithTickets: string[];
  /** Full user directory, used when "Show all users" is enabled. */
  allUsers: users[];
  /** Reassign the ticket to the chosen technician. */
  onReassign: (fullName: string, email?: string) => void;
  onClose: () => void;
}

/**
 * Overlay menu opened by right-clicking a work-item's drag handle. Lists
 * technicians to reassign the ticket to — by default only those who currently
 * have tickets, with a toggle to show the full user directory.
 *
 * Note: reassigning to "Unassigned" is intentionally absent — the reassign API
 * has no unassign path (see the `targetAssignee === 'Unassigned'` guard in
 * WorkItemDashboard.commitMove).
 */
export const ReassignMenu: React.FC<ReassignMenuProps> = ({
  anchorPosition,
  currentAssignee,
  assigneesWithTickets,
  allUsers,
  onReassign,
  onClose,
}) => {
  const [showAll, setShowAll] = useState(false);
  const { colorFor } = useUserColors();

  // Reset to the default (tickets-only) list each time the menu opens.
  useEffect(() => {
    if (anchorPosition) setShowAll(false);
  }, [anchorPosition]);

  const candidates: Candidate[] = useMemo(() => {
    const list: Candidate[] = showAll
      ? allUsers.map((u) => ({ name: u.fullname, email: u.email }))
      : assigneesWithTickets
          .filter((a) => a !== 'Unassigned')
          .map((name) => ({ name }));
    return list
      .filter((c) => c.name && c.name !== currentAssignee)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [showAll, allUsers, assigneesWithTickets, currentAssignee]);

  return (
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      open={Boolean(anchorPosition)}
      onClose={onClose}
      slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 220 } } }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ px: 2, py: 0.5, display: 'block', fontWeight: 600 }}
      >
        Reassign to
      </Typography>

      {candidates.length === 0 ? (
        <MenuItem disabled>
          <ListItemText>No other technicians</ListItemText>
        </MenuItem>
      ) : (
        candidates.map((c) => {
          const colour = colorFor({ name: c.name });
          return (
            <MenuItem
              key={c.name}
              onClick={() => {
                onReassign(c.name, c.email);
                onClose();
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Avatar
                  sx={{ width: 22, height: 22, fontSize: 11, bgcolor: colour.bg, color: colour.fg }}
                >
                  {initialsFromName(c.name)}
                </Avatar>
              </ListItemIcon>
              <ListItemText>{c.name}</ListItemText>
            </MenuItem>
          );
        })
      )}

      <Divider />
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          setShowAll((v) => !v);
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          <Checkbox edge="start" size="small" checked={showAll} disableRipple sx={{ p: 0 }} />
        </ListItemIcon>
        <ListItemText>Show all users</ListItemText>
      </MenuItem>
    </Menu>
  );
};
